import React, { useEffect, useState } from "react";
import { Activity, TrendingUp, TrendingDown, Globe2, Building2, Landmark } from "lucide-react";
import client, { fmtUsd, fmtPct } from "../lib/api";
import Sparkline from "./Sparkline";

const fmt = (n, opts = {}) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: opts.digits ?? 2,
    minimumFractionDigits: opts.digits ?? 2,
  }).format(n);
};

const isPct = (label) => /yield|rate|%/i.test(label);

const HeatCell = ({ item }) => {
  const cp = item.change_percent;
  let bg = "rgba(255,255,255,0.04)";
  let color = "#e5e5e5";
  if (typeof cp === "number") {
    const t = Math.min(Math.abs(cp) / 4, 1); // saturation cap at 4%
    if (cp >= 0) bg = `rgba(16,185,129,${0.12 + t * 0.4})`;
    else bg = `rgba(239,68,68,${0.12 + t * 0.4})`;
    color = Math.abs(cp) > 2 ? "#ffffff" : "#e5e5e5";
  }
  return (
    <div
      data-testid={`overview-cell-${item.symbol}`}
      className="rounded-xl p-4 border border-white/10 transition-all hover:scale-[1.02]"
      style={{ background: bg, color }}
    >
      <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1 truncate">{item.name}</div>
      <div className="font-mono-num text-lg">{fmt(item.price)}</div>
      <div className="text-xs font-mono-num mt-0.5">
        {cp !== null && cp !== undefined ? (cp >= 0 ? "+" : "") + cp.toFixed(2) + "%" : "—"}
      </div>
    </div>
  );
};

const SP500Hero = ({ sp }) => {
  if (!sp || sp.price === null) {
    return <div className="glass h-48 animate-pulse" />;
  }
  const cp = sp.change_percent;
  const up = cp >= 0;
  const pos = sp.fifty_two_week_position_pct ?? 50;
  return (
    <div className="glass-strong p-6 lg:p-8 relative overflow-hidden" data-testid="sp500-hero">
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          backgroundImage: up
            ? "radial-gradient(circle at 85% 10%, rgba(16,185,129,0.25), transparent 55%)"
            : "radial-gradient(circle at 85% 10%, rgba(239,68,68,0.22), transparent 55%)",
        }}
      />
      <div className="relative flex flex-col lg:flex-row gap-8 items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500">
            <Landmark size={12} /> US Equities
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tighter mt-2">S&P 500</h2>
          <div className="flex items-end gap-4 mt-3">
            <div className="font-display text-5xl font-mono-num">{fmt(sp.price)}</div>
            <div className={`text-base font-mono-num mb-1 inline-flex items-center gap-1 ${up ? "up" : "down"}`}>
              {up ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {fmt(sp.change, { digits: 2 })} ({fmtPct(cp)})
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-md">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Day Range</div>
              <div className="font-mono-num text-sm mt-1">{fmt(sp.day_low)} – {fmt(sp.day_high)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">52w Range</div>
              <div className="font-mono-num text-sm mt-1">{fmt(sp.fifty_two_week_low)} – {fmt(sp.fifty_two_week_high)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Prev Close</div>
              <div className="font-mono-num text-sm mt-1">{fmt(sp.previous_close)}</div>
            </div>
          </div>
          {/* 52-week position bar */}
          <div className="mt-5 max-w-md">
            <div className="flex justify-between text-[10px] text-neutral-500 mb-1">
              <span>52w low</span>
              <span className="font-mono-num">{pos.toFixed(0)}%</span>
              <span>52w high</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/[0.06] relative overflow-hidden">
              <div
                className="absolute top-0 bottom-0 left-0 rounded-full"
                style={{
                  width: `${pos}%`,
                  background: "linear-gradient(to right, rgba(239,68,68,0.6), rgba(251,191,36,0.6), rgba(16,185,129,0.7))",
                }}
              />
              <div className="absolute top-0 bottom-0 w-1.5 bg-white rounded-full" style={{ left: `calc(${pos}% - 3px)` }} />
            </div>
          </div>
        </div>
        <div className="w-full lg:w-80 flex-shrink-0">
          {sp.history?.length > 1 && (
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Last 30 trading days</div>
              <Sparkline data={sp.history} width={300} height={80} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionRow = ({ title, icon: Icon, items, cols = 4 }) => (
  <div data-testid={`overview-section-${title}`}>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={14} className="text-neutral-400" />
      <div className="text-xs uppercase tracking-widest text-neutral-500">{title}</div>
    </div>
    <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-${cols} gap-3`}>
      {items.map((item) => <HeatCell key={item.symbol} item={item} />)}
    </div>
  </div>
);

const StockMarketOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      client.get("/markets/stock-overview").then(({ data }) => {
        if (cancelled) return;
        setData(data);
        setLoading(false);
      }).catch((e) => {
        console.error("stock overview failed", e);
        if (!cancelled) setLoading(false);
      });
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading && !data) {
    return (
      <div className="space-y-6" data-testid="stock-overview-loading">
        <div className="glass h-56 animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="glass h-24 animate-pulse" />)}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const s = data.sections || {};

  return (
    <section className="space-y-8" data-testid="stock-market-overview">
      <div className="flex items-end justify-between">
        <div>
          <span className="chip"><Activity size={11} /> Global Equities</span>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">Stock Market</h1>
          <p className="text-neutral-400 mt-2 text-sm">S&P 500, US indices, 11 sectors, world markets, FX & yields — live from Yahoo Finance.</p>
        </div>
      </div>

      <SP500Hero sp={data.hero_sp500} />

      {s.us_indices && <SectionRow title="US Indices" icon={Landmark} items={s.us_indices} cols={4} />}
      {s.sectors && <SectionRow title="S&P 500 Sector ETFs" icon={Building2} items={s.sectors} cols={4} />}
      {s.international && <SectionRow title="International Indices" icon={Globe2} items={s.international} cols={4} />}
      {s.fixed_income_fx && <SectionRow title="Bonds · FX · Commodities" icon={Activity} items={s.fixed_income_fx} cols={4} />}
    </section>
  );
};

export default StockMarketOverview;
